// Toggle - declarative class/attribute toggling driven by [data-toggle].
// Behavioral port of v1 modules/Toggle.js; the data-attribute contract is
// unchanged (data-toggle, data-toggle-class, data-toggle-icon, ...).

import { triggerResize } from '../utils';

export const TRIGGER_EVT = 'triggerToggle';
export const TOGGLE_EVT = 'toggle';

export interface ToggleConfig {
  toggleClass: string;
  settingsAttr: string;
}

export interface ToggleEventDetail {
  target: Element;
  trigger?: Element | null;
}

export interface DoToggleOptions {
  target: Element | string;
  trigger?: Element | null;
  className?: string | null;
  icon?: string | null;
  iconTarget?: string | null;
  text?: string | null;
  expand?: boolean;
  attribute?: string | null;
  focus?: boolean;
  state?: boolean;
  resize?: boolean;
  dispatchEvent?: boolean;
}

const defaultConfig: ToggleConfig = {
  toggleClass: 'is-active',
  settingsAttr: 'data-toggle',
};

export default class Toggle {
  selector: string;

  config: ToggleConfig;

  triggers: Element[] = [];

  constructor(selector = '[data-toggle]', config: Partial<ToggleConfig> = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  /** Check if an element is toggled/active. */
  static isToggled(
    target: Element | null,
    activeClass: string = defaultConfig.toggleClass,
  ): boolean {
    if (!target) return false;

    if (target.hasAttribute('data-toggle-expand')) {
      return target.getAttribute('aria-expanded') === 'true';
    }

    return target.classList.contains(activeClass);
  }

  /** Toggle one or more properties on an element. */
  static doToggle(options: DoToggleOptions): Element | false {
    const {
      trigger,
      className,
      icon,
      iconTarget,
      text,
      expand,
      attribute,
      focus,
      state,
      resize = false,
      dispatchEvent = true,
    } = options;

    const target =
      options.target instanceof Element
        ? options.target
        : document.querySelector(options.target);

    if (!target) {
      return false;
    }

    if (className != null) {
      if (state !== undefined) {
        target.classList[state ? 'add' : 'remove'](className);
      } else {
        target.classList.toggle(className);
      }
    }

    if (icon) {
      // Icon lives either in a sprite <use> (xlink:href) or a FA <svg> (data-icon).
      const iconSelectors = iconTarget
        ? [`use${iconTarget}`, `svg${iconTarget}`]
        : ['use', 'svg'];

      let iconEl: Element | null = null;
      let currentIconAttribute = '';

      for (const selector of iconSelectors) {
        iconEl = target.querySelector(selector);
        if (iconEl) {
          currentIconAttribute = selector.startsWith('use') ? 'xlink:href' : 'data-icon';
          break;
        }
      }

      if (iconEl) {
        // Store the current icon on the target so the next toggle swaps back.
        const currentIcon = iconEl.getAttribute(currentIconAttribute);
        target.setAttribute('data-toggle-icon', currentIcon ?? '');
        iconEl.setAttribute(currentIconAttribute, icon);
      }
    }

    if (text) {
      const toggleTextTarget =
        target.querySelector('[data-toggle-text-target]') || target;
      const oldText = toggleTextTarget.innerHTML;

      target.setAttribute('data-toggle-text', oldText);
      toggleTextTarget.innerHTML = text;
    }

    if (expand) {
      const currentState = target.getAttribute('aria-expanded') || 'false';
      const newState =
        state !== undefined
          ? state.toString()
          : currentState === 'true' ? 'false' : 'true';

      target.setAttribute('aria-expanded', newState);
    }

    if (attribute) {
      if (target.getAttribute(attribute) !== null) {
        target.removeAttribute(attribute);
      } else {
        target.setAttribute(attribute, '');
      }
    }

    if (focus && target instanceof HTMLElement) {
      target.focus();
    }

    if (dispatchEvent) {
      target.dispatchEvent(
        new CustomEvent<ToggleEventDetail>(TOGGLE_EVT, {
          detail: { target, trigger },
          bubbles: true,
        }),
      );
    }

    if (resize) {
      triggerResize();
    }

    return target;
  }

  destroy = (): void => {
    this.triggers.forEach(trigger => {
      trigger.removeEventListener('click', this._onClick);
    });
    this.triggers = [];
  };

  update = (): void => {
    this.destroy();
    this._init();
  };

  private _init = (): void => {
    this.triggers = [...document.querySelectorAll(this.selector)];

    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', this._onClick);
    });
  };

  private _onClick = (e: Event): void => {
    const trigger = e.currentTarget as Element;
    const triggerTargets = this._getTargets(trigger);

    triggerTargets.forEach(target => {
      let className: string | null = this.config.toggleClass;

      if (trigger.hasAttribute('data-toggle-class-target')) {
        className = trigger.getAttribute('data-toggle-class-target');
      }

      if (target.hasAttribute('data-toggle-class')) {
        className = target.getAttribute('data-toggle-class') || null;
      }

      Toggle.doToggle({
        target,
        trigger,
        className,
        icon: target.getAttribute('data-toggle-icon'),
        iconTarget: target.getAttribute('data-toggle-icon-target'),
        text: target.getAttribute('data-toggle-text'),
        expand: target.hasAttribute('data-toggle-expand'),
        attribute: target.getAttribute('data-toggle-attribute'),
        focus: target.hasAttribute('data-toggle-focus'),
      });
    });

    trigger.dispatchEvent(new CustomEvent(TRIGGER_EVT, { bubbles: true }));
  };

  /** Resolve a trigger's targets: selector list or self/parent/previous/next. */
  private _getTargets = (trigger: Element): Element[] => {
    const targetAttribute = trigger.getAttribute(this.config.settingsAttr);
    if (!targetAttribute) return [];

    return targetAttribute
      .split(',')
      .map(selector => {
        const target = selector.trim();

        switch (target) {
          case 'self':
            return trigger;

          case 'parent': {
            let parentElement: Element | null = trigger;
            const parentHop = parseInt(
              trigger.getAttribute('data-parent-hop') || '1',
              10,
            );

            for (let j = 0; j < parentHop && parentElement; j++) {
              parentElement = parentElement.parentElement;
            }
            return parentElement;
          }

          case 'previous':
            return trigger.previousElementSibling;

          case 'next':
            return trigger.nextElementSibling;

          default:
            return document.querySelector(target);
        }
      })
      .filter((el): el is Element => Boolean(el));
  };
}

export const { doToggle, isToggled } = Toggle;
